import { ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CommissionActions } from "./CommissionActions";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import { Separator } from "@/components/ui/separator";
import { useAppStore } from "@/stores";
import { format, parse } from "date-fns";
import {
  getCommissionsValue,
  getPercentage,
  getUniqueDeals,
} from "@revelate/calc";
import { getBadgeVariant, getCurrencyFormatted, isIgnored } from "@/lib/app";
import {
  CalculationDeal,
  CommissionStatus,
  CommissionUser,
  DateRange,
  NestedCommission,
  NestedUser,
} from "@revelate/types";
import { capitalizeFirstLetter } from "@revelate/utils";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TabsContent } from "@radix-ui/react-tabs";
import {
  getBadgeTitle,
  groupCommissionsByMonth,
  groupCommissionsByTargetStatus,
} from "@/lib/app";
import { getRoute } from "@/lib/routes";
import { CommissionComments } from "./CommissionComments";
import { useAPI } from "@/hooks/useAPI";
import { Loader } from "@/components/Loader";

interface ReportCardProps {
  timePeriod: string;
  employees: CommissionUser[];
  ids: string[];
  scopedUsers: NestedUser[];
  dateRange: DateRange;
  commissions: NestedCommission[];
  updateCommissions: (
    previousStatus: CommissionStatus,
    newStatus: CommissionStatus,
    filterByCommission?: NestedCommission
  ) => void;
  updatingCommissionId?: string | null;
}

export const ReportCard = ({
  timePeriod,
  employees,
  ids,
  commissions,
  updateCommissions,
  updatingCommissionId = null,
}: ReportCardProps) => {
  const { currentUser, companyCurrency } = useAppStore((state) => state);
  const currentEmployeeIndex = employees.findIndex((employee) =>
    ids.includes(employee.id)
  );
  const [, setLocation] = useLocation();
  const { isCalculating } = useAPI();

  // Group commissions by month first
  const groupedCommissionsByMonth = groupCommissionsByMonth(commissions);

  const isLoading = (commission: NestedCommission | undefined) => {
    if (!commission?.id || !updatingCommissionId) return false;
    return updatingCommissionId === commission.id.toString();
  };

  const showPreviousEmployee = () => {
    const previousEmployee =
      currentEmployeeIndex === 0
        ? employees[employees.length - 1]
        : employees[currentEmployeeIndex - 1];
    if (previousEmployee)
      setLocation(getRoute("reports", "user", previousEmployee.id));
  };

  const renderSubtitle = (commissions: NestedCommission[]) => {
    const dates: string[] = [];

    if (!commissions || commissions.length === 0) return "";
    // Filter out non-unique deal_ids
    commissions?.forEach((commission) => {
      if (commission?.deal && commission?.deal?.closed_at) {
        // Check if dates contains the exact string
        const formattedDate = format(commission?.deal?.closed_at, "yyyy-MM-dd");
        if (!dates.includes(formattedDate)) dates.push(formattedDate);
      }
    });
    if (dates.length > 0) {
      return <span>Closed {dates.join(" and ")}</span>;
    }
    return null;
  };

  // NOTE: This function acts like 2 different functions
  // Either provider id OR a summary of the commission calculations
  const renderProviderIdOrCalculationSummary = (
    commissions: NestedCommission[]
  ) => {
    // Provider ID
    const providerIds: string[] = [];

    for (const commission of commissions) {
      const providerId = commission?.deal?.provider_id;
      if (providerId) {
        if (!providerIds.includes(providerId)) providerIds.push(providerId);
      }
    }

    if (providerIds.length > 0) {
      const providerIdsText =
        providerIds && providerIds.length > 2
          ? providerIds.slice(0, 2).join(" and ") +
            ` and ${providerIds.length - 2} more`
          : providerIds.join(" and ");
      const providerIdLabel =
        commissions[0]?.deal && providerIds.length > 0
          ? `${capitalizeFirstLetter(
              commissions[0]?.deal?.provider
            )} ID ${providerIdsText}`
          : commissions[0]?.deal
            ? "No provider ID"
            : "";
      return <span>{providerIdLabel}</span>;
    }

    // Calculation summary
    const { accelerator } = commissions[0] || {};
    const { commission_based_on } = accelerator || {};
    const accumulatedActualValue = commissions?.reduce(
      (acc, commission) => acc + (commission.value_actual || 0),
      0
    );

    // Need unique deal keys because deals sometimes overlap
    const uniqueDealKeys: Set<string> = new Set();

    for (const c of commissions) {
      const commissionDealKeys = c.deals_key.split("|");
      for (const k of commissionDealKeys) {
        if (!uniqueDealKeys.has(k)) {
          uniqueDealKeys.add(k);
        }
      }
    }

    const dealCount = uniqueDealKeys.size;

    if (
      commission_based_on === "deal_value_above_target" ||
      commission_based_on === "deal_value_below_target" ||
      commission_based_on === "fixed_value"
    ) {
      return (
        <span>
          Based on {dealCount} {dealCount === 1 ? "deal" : "deals"} with a value
          of{" "}
          {getCurrencyFormatted(accumulatedActualValue, companyCurrency?.code)}
        </span>
      );
    }

    return null;
  };

  // For "aggregate" plans such as those using all deals of the period or above/below
  const renderDealDetails = (commissions: NestedCommission[]) => {
    const { accelerator } = commissions[0] || {};
    const { commission_based_on } = accelerator || {};

    const allDealsUsedInCommmissionCalculations: Pick<
      CalculationDeal,
      "id" | "name" | "value" | "currency_code"
    >[] = [];

    for (const c of commissions) {
      if (
        c.deal_details &&
        Array.isArray(c.deal_details) &&
        c.deal_details.length > 0
      ) {
        const deals = c.deal_details as CalculationDeal[];
        allDealsUsedInCommmissionCalculations.push(...deals);
      }
    }

    // Get unique deals, since plans which payout multiple commissions
    // will have duplicates as each commission brings its own set of copies
    const uniqueDeals = getUniqueDeals(allDealsUsedInCommmissionCalculations);

    const shouldShowDealDetails =
      uniqueDeals.length > 0 &&
      (commission_based_on === "deal_value_above_target" ||
        commission_based_on === "deal_value_below_target" ||
        commission_based_on === "fixed_value");

    if (!shouldShowDealDetails) return null;

    return (
      <div className="mb-4">
        {uniqueDeals
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((deal) => (
            <div className="text-muted-foreground text-xs" key={deal.id}>
              <span>• </span>
              <span className="font-semibold">{deal.name} </span>
              <span>
                • {getCurrencyFormatted(deal.value, deal?.currency_code)}
              </span>
            </div>
          ))}
      </div>
    );
  };

  const showNextEmployee = () => {
    const nextEmployee =
      currentEmployeeIndex === employees.length - 1
        ? employees[0]
        : employees[currentEmployeeIndex + 1];
    if (nextEmployee) setLocation(getRoute("reports", "user", nextEmployee.id));
  };

  const commissionRows = Object.entries(groupedCommissionsByMonth);

  return (
    <div>
      <Tabs defaultValue="summary">
        <Card
          className="overflow-hidden relative"
          x-chunk="dashboard-05-chunk-4"
        >
          <div className="absolute top-[18px] right-5">
            <div className="flex flex-row gap-x-3 items-center">
              {/* Loading indicator */}
              {currentUser?.role === "super_admin" && isCalculating && (
                <div>
                  <Loader size={22} parentClassName="h-8" />
                </div>
              )}
              <TabsList>
                <TabsTrigger value="summary">
                  <span className="text-black">
                    Summary
                  </span>
                </TabsTrigger>
                <TabsTrigger value="detailed">
                  <span className="text-black">
                    Details
                  </span>
                </TabsTrigger>
              </TabsList>
            </div>
          </div>
          <CardHeader>
            <CardTitle>Commissions</CardTitle>
            <TabsContent value="summary">
              <CardDescription>Summary view</CardDescription>
            </TabsContent>
            <TabsContent value="detailed">
              <CardDescription>Detailed view</CardDescription>
            </TabsContent>
          </CardHeader>
          <CardContent className="p-6 pt-2 text-sm">
            <div className="grid gap-3">
              {commissionRows && commissionRows.length > 0 ? (
                commissionRows.map(([key, value]) => (
                  <div key={key}>
                    <div className="mb-4 align-middle">
                      <span className="font-medium text-[16px] mr-2 align-middle">
                        {format(parse(key, "yyyy-MM", new Date()), "MMMM yyyy")}
                      </span>
                      <Badge
                        className="font-normal text-xs py-0.5"
                        variant={
                          getBadgeVariant(value) === "paid"
                            ? "secondary"
                            : "default"
                        }
                      >
                        {getBadgeTitle(value)}
                      </Badge>
                    </div>
                    <ul className="grid gap-4">
                      {(() => {
                        // Group commissions by target status (above/below target)
                        const { sortedEntries, displayNames } =
                          groupCommissionsByTargetStatus(value);

                        // Use sortedEntries to maintain the order by closing date
                        return sortedEntries.map(
                          ([dealKey, commissions], index) => {
                            // Get the display name from our map
                            const displayName =
                              displayNames[dealKey] || dealKey;

                            return (
                              <li key={index}>
                                <ul className="grid gap-1">
                                  <li className="flex flex-col justify-between">
                                    <div className="flex flex-row my-1 text-gray-800 text-sm">
                                      {/* Left side: Commission info  */}
                                      <div className="flex items-center gap-2">
                                        <div className="flex flex-col">
                                          {/* Name and comments */}
                                          <div className="flex items-center gap-0">
                                            {/* Name */}
                                            <span
                                              className={`font-medium mr-0 align-middle ${isIgnored(commissions) ? "line-through text-muted-foreground" : ""}`}
                                            >
                                              {displayName}
                                            </span>

                                            {/* Display comment icon if there are comments */}
                                            {commissions[0] &&
                                            commissions[0].comment_count &&
                                            commissions[0].comment_count > 0 ? (
                                              <div
                                                className="inline-flex items-center gap-1 ml-0 cursor-pointer text-muted-foreground hover:text-primary transition-colors"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  window.dispatchEvent(
                                                    new CustomEvent(
                                                      "commission-comment-added"
                                                    )
                                                  );
                                                }}
                                              >
                                                <CommissionComments
                                                  commission={commissions[0]}
                                                  hideText={true}
                                                />

                                                <MessageCircle className="h-4 w-4" />
                                                <span className="text-sm font-bold">
                                                  {commissions[0].comment_count}
                                                </span>
                                              </div>
                                            ) : null}
                                          </div>

                                          <TabsContent value="detailed">
                                            <div className="flex flex-row gap-2 px-0 text-muted-foreground text-xs mb-2">
                                              {renderSubtitle(commissions)}
                                              {renderProviderIdOrCalculationSummary(
                                                commissions
                                              )}
                                            </div>
                                            {renderDealDetails(commissions)}
                                          </TabsContent>
                                        </div>
                                      </div>

                                      {/* Right side: Commission value and actions */}
                                      <div className="ml-auto flex items-start">
                                        <div className="flex flex-row items-center gap-2">
                                          <span className="font-semibold">
                                            {isIgnored(commissions)
                                              ? getCurrencyFormatted(
                                                  0,
                                                  companyCurrency?.code
                                                )
                                              : getCurrencyFormatted(
                                                  getCommissionsValue(
                                                    commissions
                                                  ),
                                                  companyCurrency?.code
                                                )}
                                          </span>
                                          <span>
                                            {currentUser?.role ===
                                              "super_admin" ||
                                            currentUser?.role === "admin" ? (
                                              <CommissionActions
                                                commissions={commissions}
                                                updateCommissions={
                                                  updateCommissions
                                                }
                                                isLoading={isLoading(
                                                  commissions[0]
                                                )}
                                              />
                                            ) : (
                                              <></>
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex flex-col gap-1">
                                      {commissions?.map(
                                        (commission, commissionIndex) => (
                                          <div
                                            className={`flex flex-row my-0 text-xs`}
                                            key={commissionIndex}
                                          >
                                            <div className="flex flex-col">
                                              <TabsContent value="summary">
                                                <div className="">
                                                  <span
                                                    className={`text-gray-500 mr-1 ${commission.status === "ignored" ? "line-through text-muted-foreground" : ""}`}
                                                  >
                                                    {getPercentage(
                                                      commission.rate
                                                    )}{" "}
                                                    of{" "}
                                                    {getCurrencyFormatted(
                                                      commission.commission_basis,
                                                      companyCurrency?.code
                                                    )}{" "}
                                                  </span>{" "}
                                                  <Badge
                                                    variant={commission.status}
                                                    className="text-[10px] py-0 px-1.5"
                                                  >
                                                    {capitalizeFirstLetter(
                                                      commission.status
                                                    )}
                                                  </Badge>
                                                </div>
                                              </TabsContent>

                                              <TabsContent value="detailed">
                                                <div className="">
                                                  <strong
                                                    className={`text-gray-500 mr-2 my-1 ${commission.status === "ignored" ? "line-through text-muted-foreground" : ""}`}
                                                  >
                                                    {getPercentage(
                                                      commission.rate
                                                    )}{" "}
                                                    of{" "}
                                                    {getCurrencyFormatted(
                                                      commission.commission_basis,
                                                      companyCurrency?.code
                                                    )}{" "}
                                                  </strong>{" "}
                                                  <Badge
                                                    variant={commission.status}
                                                    className="text-[10px] py-0 px-1.5"
                                                  >
                                                    {capitalizeFirstLetter(
                                                      commission.status
                                                    )}
                                                  </Badge>
                                                </div>
                                                <span className="text-muted-foreground">
                                                  {commission.accelerator
                                                    ? `${commission.deal_id ? `${commission.plan.name} • ` : ""}${commission.accelerator.name}` //  (ID: ${commission.accelerator.id})
                                                    : `Accelerator ${
                                                        commissionIndex + 1
                                                      }`}{" "}
                                                </span>
                                              </TabsContent>
                                            </div>
                                            <div className="ml-auto flex items-top gap-2">
                                              <div
                                                className={`text-muted-foreground ${commission.status === "ignored" ? "line-through text-muted-foreground" : ""}`}
                                              >
                                                {getCurrencyFormatted(
                                                  commission.amount,
                                                  companyCurrency?.code
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </li>
                                </ul>
                              </li>
                            );
                          }
                        );
                      })()}
                    </ul>
                    <Separator className="my-4" />
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-left gap-1 mb-4">
                  <p className="text-md font-semibold">
                    No pending commissions
                  </p>
                  <p className="text-muted-foreground">
                    {`You do not have any commissions to approve this period.`}
                  </p>
                </div>
              )}

              <ul className="grid gap-3">
                <li className="flex items-center justify-between font-semibold">
                  <div>
                    <span className="text-muted-foreground">
                      Total for period
                    </span>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <div>
                      <span>
                        {getCurrencyFormatted(
                          getCommissionsValue(commissions),
                          companyCurrency?.code
                        )}
                      </span>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </CardContent>
          <CardFooter className="flex flex-row items-center border-t bg-muted/50 px-6 py-3">
            <div className="text-xs text-muted-foreground">
              <time dateTime={timePeriod}>{timePeriod}</time>
            </div>
            <Pagination className="ml-auto mr-0 w-auto">
              <PaginationContent>
                <PaginationItem>
                  <Button
                    onClick={() => showPreviousEmployee()}
                    size="icon"
                    variant="outline"
                    className="h-6 w-6"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    <span className="sr-only">Previous Employee</span>
                  </Button>
                </PaginationItem>
                <PaginationItem>
                  <Button
                    onClick={() => showNextEmployee()}
                    size="icon"
                    variant="outline"
                    className="h-6 w-6"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                    <span className="sr-only">Next Employee</span>
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </CardFooter>
        </Card>
      </Tabs>
    </div>
  );
};
